/**
 * ⛔ A INSTALAÇÃO É AGENDADA POR TURNO (Douglas 26/09/2026).
 *
 * Espelho de apps/backend/src/domain/agenda/turno-instalacao.ts e da função
 * fn_turno_instalacao do banco — o teste prende as três listas iguais.
 *
 * data_agendamento de instalação guarda o INÍCIO do turno, em São Paulo.
 * A tela do pedido abria a agenda em UTC (+3h) e regravava a hora andada: foi
 * assim que 15:00 virou 18:00. Aqui a leitura e a gravação são sempre em
 * São Paulo, com offset explícito.
 */
export const TURNOS_INSTALACAO = [
  { inicio: '08:00', fim: '12:00' },
  { inicio: '12:00', fim: '15:00' },
  { inicio: '15:00', fim: '18:00' },
] as const;

export type TurnoInstalacao = (typeof TURNOS_INSTALACAO)[number];

const TZ = 'America/Sao_Paulo';

function comoData(data: Date | string | null | undefined): Date | null {
  const d = data ? new Date(data) : null;
  return d && !Number.isNaN(d.getTime()) ? d : null;
}

/** Data (YYYY-MM-DD) e hora (HH:mm) da agenda em São Paulo — para preencher campos. */
export function partesEmSaoPaulo(data: Date | string | null | undefined): { data: string; hora: string } | null {
  const d = comoData(data);
  if (!d) return null;
  const p = Object.fromEntries(
    new Intl.DateTimeFormat('en-CA', {
      timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    }).formatToParts(d).map((x) => [x.type, x.value]),
  );
  return { data: `${p.year}-${p.month}-${p.day}`, hora: `${p.hour}:${p.minute}` };
}

/** Valor para gravar: início do turno em São Paulo, com offset explícito. */
export function valorDoTurno(dataYmd: string, turno: TurnoInstalacao): string {
  return `${dataYmd}T${turno.inicio}:00-03:00`;
}

export function turnoDaAgenda(data: Date | string | null | undefined): TurnoInstalacao | null {
  const d = comoData(data);
  if (!d) return null;
  const hora = new Intl.DateTimeFormat('en-GB', { timeZone: TZ, hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' }).format(d);
  return TURNOS_INSTALACAO.find((t) => `${t.inicio}:00` === hora) ?? null;
}

const horaCurta = (hhmm: string) => `${Number(hhmm.slice(0, 2))}h`;

/** "15h às 18h" */
export function rotuloTurno(turno: TurnoInstalacao): string {
  return `${horaCurta(turno.inicio)} às ${horaCurta(turno.fim)}`;
}

/** "15h–18h" — para célula estreita de tabela. */
export function rotuloTurnoCurto(turno: TurnoInstalacao): string {
  return `${horaCurta(turno.inicio)}–${horaCurta(turno.fim)}`;
}

/** "29/09 · 15h às 18h" — ou "29/09 · sem turno" quando a hora não é início de turno. */
export function agendaInstalacaoTexto(data: Date | string | null | undefined, opcoes: { ano?: boolean } = {}): string {
  const d = comoData(data);
  if (!d) return '';
  const dia = new Intl.DateTimeFormat('pt-BR', {
    timeZone: TZ, day: '2-digit', month: '2-digit', ...(opcoes.ano ? { year: 'numeric' as const } : {}),
  }).format(d);
  const turno = turnoDaAgenda(d);
  return `${dia} · ${turno ? rotuloTurno(turno) : 'sem turno'}`;
}

export function ehInstalacao(agendaTipo: string | null | undefined): boolean {
  return String(agendaTipo || '').toUpperCase() === 'INSTALACAO';
}
